<template>
  <div>
    <h1>Earns Methoden</h1>
    <p>
      Maßnahmen können mit der überschlägigen oder der genormten / wissenschaftlichen
      Methode berechnet werden. Die überschlägigen Methoden benötigen weniger Daten
      und sind somit schneller zu untersuchen. Die genormten / wissenschaftlichen Methoden
      bieten auf der anderen Seite eine zuverlässige Berechnung welche belastbar sind.
    </p>
    <p>
      Um eine Maßnahme abspeichern zu können, sind in alle technischen
      Pflichtfelder valide Werte einzutragen. Zusätzlich können optional die
      finanziellen Parameter eingetragen werden.
    </p>
    <h1>Pumpe</h1>
    <p>
      Diese Methode bietet drei Berechnungsansätze für die detaillierte Bewertung von Pumpen:
      <b>Pumpenkennlinien Leistung</b> (direkte Leistungswerte aus Herstellerangaben),
      <b>Systemwirkungsgrad</b> (physikbasierte Modellierung mit Affinitätsgesetzen,
      Drehzahlregelung und lastabhängigen Frequenzumrichter-Wirkungsgraden) und
      <b>Nassläufer: EEI</b> (Umrechnung des im Datenblatt angegebenen
      Energieeffizienzindex nach Verordnung (EG) 641/2009).
    </p>
    <p>
      Die Systemwirkungsgrad-Methode berücksichtigt nun vollständig:
    </p>
    <ul>
      <li><b>Affinitätsgesetze:</b> Q∝n, H∝n², P∝n³ für Drehzahländerungen</li>
      <li><b>Diskrete Drehzahlstufen:</b> Zweistufige Regelung mit 100% und 67% Geschwindigkeit</li>
      <li><b>VSD-Effizienz:</b> Lastabhängige Wirkungsgrade 92-96% statt konstant 95%</li>
      <li><b>Drosselverluste:</b> Explizite Berechnung von E = ρ·g·Q·ΔH·t</li>
    </ul>

    <v-expansion-panels accordion class="mt-5">
      <!--
        Genauigkeitsüberblick — bewusst das erste Panel. Alle Zahlen sind gegen zwei reale
        Herstellerdatenblätter gemessen, nicht aus dem Code abgeleitet: siehe
        methods/tests/methods/test_pump_datasheet_anchors.py und
        methods/docs/pump_calculation_review.md §4.
      -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Genauigkeit der Methoden im Überblick</div>
        </v-expansion-panel-title>
        <!-- eager: Inhalt soll auch ungeöffnet im DOM stehen (Seitensuche, Druck) -->
        <v-expansion-panel-text eager>
          <section class="precision">
            <p>
              Die Wahl der Methode beeinflusst das Ergebnis stärker als jede einzelne Eingabe:
              auf derselben realen Pumpe liegen zwei zulässige Methoden um den
              <b>Faktor 1,23</b> auseinander. Die folgenden Angaben wurden gegen zwei
              Herstellerdatenblätter gemessen — <em>Wilo-Stratos PICO 25/1-6</em> (Nassläufer)
              und <em>Grundfos NB 32-125.1/140</em> (Trockenläufer).
            </p>

            <table>
              <thead>
                <tr>
                  <th>Methode</th>
                  <th>Genauigkeit</th>
                  <th>Worauf es ankommt</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Pumpenkennlinien Leistung</td>
                  <td><b>Sehr hoch</b> — kein Modellfehler</td>
                  <td>
                    <b>E = P · t</b> — hier gibt es kein Modell, das falsch sein könnte. Ein um
                    x % falsch abgelesener Messwert verschiebt das Ergebnis um genau x %, nicht
                    mehr. Die Genauigkeit ist die Ihres Messgeräts.
                  </td>
                </tr>
                <tr>
                  <td>EEI (EU 641/2009)</td>
                  <td><b>Sehr hoch</b> — bei korrektem Betriebspunkt</td>
                  <td>
                    Die Umrechnung folgt exakt der Verordnung. Entscheidend ist der
                    <b>Referenz-Betriebspunkt</b> (dort, wo Q · H maximal ist) — <em>nicht</em>
                    der Auslegungspunkt Ihrer Anlage: ein plausibler Auslegungspunkt statt des
                    Referenzpunktes ergab <b>−51 %</b>. Ein zu hoch abgelesener Volumenstrom
                    wirkt dagegen gedämpft (+10 % Q ergibt nur +7 %).
                  </td>
                </tr>
                <tr>
                  <td>Systemwirkungsgrad — mit Wirkungsgrad im Bestpunkt (BEP) aus dem Datenblatt</td>
                  <td><b>Hoch</b> — +0,3 %</td>
                  <td>
                    Trifft die reale Pumpe auf <b>+0,3 %</b> genau. Keine einzelne Eingabe ist
                    kritisch: Q oder H 10 % zu hoch ergibt +10 %, eine verwechselte Polzahl
                    −1 %, eine verwechselte Effizienzklasse höchstens +14 %.
                  </td>
                </tr>
                <tr>
                  <td>Systemwirkungsgrad — ohne BEP aus dem Datenblatt</td>
                  <td><b>Schätzung</b> — rund 19 % zu niedrig</td>
                  <td>
                    Ohne BEP wird der hydraulische Wirkungsgrad aus dem Mindestwirkungsgrad nach
                    EU 547/2012 geschätzt. Am geprüften Datenblatt lag das Ergebnis
                    <b>19 % zu niedrig</b> — die Pumpe verbraucht real mehr als berechnet.
                    <b>Der BEP aus dem Datenblatt ist der wichtigste Einzelwert dieser
                    Methode</b>; er ersetzt die Schätzung vollständig.
                  </td>
                </tr>
              </tbody>
            </table>

            <p>
              <b>Nassläufer:</b> Der Systemwirkungsgrad ist für Nassläufer (Umwälzpumpen) nicht
              anwendbar — das zugrunde liegende Motormodell gilt erst ab 0,12 kW, kleine
              Umwälzpumpen liegen mit 3–140 W darunter. Für Nassläufer sind <b>EEI</b> oder das
              <b>Typenschild</b> (Methode „Pumpe überschlägig“) die richtigen Wege.
            </p>

            <p class="callout">
              <b>Nutzen bei Pumpen-Maßnahmen.</b> Die <em>Differenz</em> des Nutzens zwischen
              Bestand und Planung ist bei Pumpen <b>nicht belastbar</b> und sollte nicht als
              „eingesparte Nutzenergie“ gelesen werden: Bestand und Planung werden mit
              unterschiedlichen Wirkungsgrad-Annahmen bewertet, sodass ein Austausch gleicher
              Förderleistung rechnerisch eine Änderung des Nutzens ausweist. Aussagekräftig ist
              die <b>Stromeinsparung</b>. <b>Kosten, CO₂ und Primärenergie sind nicht
              betroffen</b> — sie werden aus dem Stromverbrauch gebildet.
            </p>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Schritte vor der Berechnung</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ol type="1">
            <li>
              <b>Einsparpotential und Wirtschaftlichkeit mit EARNS-Potentialanalyse
                abschätzen anhand der Typenschilddaten</b>
              <p>Erste Annahmen hierbei:</p>
              <ul>
                <li>
                  Die Bestands-Pumpe läuft im Auslegungspunkt gemäß Typenschild.
                </li>
              </ul>
            </li>
            <li>
              <b>Bedarf überprüfen und mit aktuellem Betriebspunkt vergleichen</b>
              <p>
                Häufig ist die Bestands-Pumpe größer dimensioniert als erforderlich.
                Ziel ist es, die neue Pumpe so klein wie erforderlich auszulegen.
                Hierzu lohnt es sich, mindestens folgende Punkte zu prüfen:
              </p>
              <ul>
                <li>Ist die Pumpe überdimensioniert?</li>
                <li>Läuft die Pumpe länger als eigentlich erforderlich?</li>
                <li>Wurden Gebäudeteile stillgelegt, ohne den Volumenstrom anzupassen?</li>
                <li>Kann durch hydraulischen Abgleich die benötigte Menge reduziert werden?</li>
              </ul>
            </li>
            <li>
              <b>Auslegungs-Betriebspunkt für die neue Pumpe definieren</b>
            </li>
            <li>
              <b>Überprüfung der Pumpe</b>
              <ul>
                <li>
                  Anhand von Herstellersoftware sollte die für den jeweiligen
                  Anwendungsfall (Einbausituation, Betriebspunkt, Kosten) optimal
                  geeignete Pumpe ermittelt werden.
                </li>
              </ul>
            </li>
            <li>
              <b>Systemlösung auswählen und finale Einsparhöhe berechnen</b>
            </li>
          </ol>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Eingabeparameter in Pumpe</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <h3>Gemeinsame Parameter für beide Methoden</h3>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Berechnungsmethode</td>
                <td>
                  Auswahl zwischen <b>Pumpenkennlinien Leistung</b>,
                  <b>Systemwirkungsgrad</b> und <b>Nassläufer: EEI (EU 641/2009)</b>.
                </td>
              </tr>
              <tr>
                <td>Anzahl</td>
                <td>Anzahl der Pumpen des gleichen Typs und der gleichen Betriebsweise.</td>
              </tr>
              <tr>
                <td>Baujahr / Wartungszustand</td>
                <td>
                  <b>Werden hier nicht erhoben.</b> Beide Angaben fließen ausschließlich in die
                  Schätzung des Gesamtwirkungsgrades ein, die nur greift, wenn kein
                  Betriebspunkt bekannt ist. Alle drei Methoden dieser Maßnahme liefern die
                  hydraulische Arbeit jedoch immer explizit — aus Q und H oder aus der
                  Kennlinie —, sodass die Schätzung hier nie zum Tragen käme. Für die
                  überschlägige Pumpe sind beide Angaben dagegen relevant.
                </td>
              </tr>
              <tr>
                <td>Norm-Betriebsstunden</td>
                <td>
                  Optional: Aktivieren, um Normwerte zu verwenden. Bei Aktivierung wird
                  die Auswahl der Norm-Betriebsstunden eingeblendet und die manuelle
                  Eingabe der Betriebsstunden gesperrt.
                </td>
              </tr>
              <tr>
                <td>Betriebsstunden</td>
                <td>
                  Jährliche Betriebsstunden in h/a (nur bei deaktivierten Norm-Betriebsstunden).
                </td>
              </tr>
              <tr>
                <td>Rechenmethode</td>
                <td>
                  Die <b>neue Rechenmethode</b> rechnet seit dem 09/2026 nach DIN V 18599 und ist
                  nicht mehr auswählbar — sie gilt für jede neue Maßnahme. Bereits gespeicherte
                  Maßnahmen behalten automatisch die <b>alte Rechenmethode</b>, damit erstellte
                  Berichte reproduzierbar bleiben; das wird in der Maßnahme angezeigt.
                  Die Einstellung gilt für Referenz und geplante Maßnahme gemeinsam — ein
                  Vergleich zweier unterschiedlich gerechneter Varianten wäre nicht aussagekräftig.
                </td>
              </tr>
              <tr>
                <td>Pumpenmanagement</td>
                <td>
                  Verkürzt die Laufzeit der Umwälzpumpe, wenn der Wärmeerzeuger die Pumpe bei
                  fehlendem Wärmebedarf abschaltet. Nach
                  <em>DIN V 18599-5:2018-09 §6.3.2.2, Gl. (56)</em>:
                  ohne Pumpenmanagement 1,00 · außentemperaturgeführt 0,75 ·
                  raumtemperaturgeführt 0,45. Gilt für alle Berechnungsmethoden, da die
                  Betriebszeit in jede Methode eingeht. Voreinstellung: „ohne“.
                </td>
              </tr>
              <tr>
                <td>Hydraulischer Abgleich</td>
                <td>
                  Nur bei der Methode <em>Systemwirkungsgrad</em>. Ein nicht abgeglichenes Netz
                  muss überpumpt werden, damit auch der ungünstigste Verbraucher versorgt wird.
                  Nach <em>DIN V 18599-7:2018-09 D.2.6</em> wird der berechnete hydraulische
                  Bedarf dann mit <code>f<sub>Abgl</sub></code> = 1,25 angesetzt
                  (abgeglichen: 1,00). Voreinstellung: „hydraulisch abgeglichen“.
                </td>
              </tr>
            </tbody>
          </table>

          <p>
            <strong>Warum der hydraulische Abgleich nur bei „Systemwirkungsgrad“ erscheint.</strong>
            Der Faktor korrigiert eine <em>berechnete</em> hydraulische Leistung. Bei den Methoden
            <em>Pumpenkennlinien-Leistung</em> und <em>EEI</em> wird die Leistung gemessen bzw.
            vom Hersteller deklariert — das Überpumpen steckt dort bereits im Wert. Eine erneute
            Anwendung würde denselben Effekt doppelt zählen. Aus demselben Grund erhöht der Faktor
            zwar den Stromverbrauch, nicht aber den Nutzen: die zusätzliche Förderarbeit ist
            Verlust, keine nutzbare Leistung.
          </p>

          <h3>Methode 1: Pumpenkennlinien Leistung</h3>
          <p>
            Diese Methode nutzt die direkten Leistungswerte aus Pumpenkennlinien.
            Ideal, wenn Herstellerangaben für verschiedene Lastpunkte vorliegen.
          </p>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Leistung bei Volllast</td>
                <td>Elektrische Leistungsaufnahme bei 100% Last (kW)</td>
              </tr>
              <tr>
                <td>Leistung bei 75% Last</td>
                <td>Elektrische Leistungsaufnahme bei 75% Last (kW)</td>
              </tr>
              <tr>
                <td>Leistung bei 50% Last</td>
                <td>Elektrische Leistungsaufnahme bei 50% Last (kW)</td>
              </tr>
              <tr>
                <td>Leistung bei 25% Last</td>
                <td>Elektrische Leistungsaufnahme bei 25% Last (kW)</td>
              </tr>
              <tr>
                <td>Anteil Volllast</td>
                <td>Zeitanteil im Betrieb bei Volllast (%)</td>
              </tr>
              <tr>
                <td>Anteil 75% Last</td>
                <td>Zeitanteil im Betrieb bei 75% Last (%)</td>
              </tr>
              <tr>
                <td>Anteil 50% Last</td>
                <td>Zeitanteil im Betrieb bei 50% Last (%)</td>
              </tr>
              <tr>
                <td>Anteil 25% Last</td>
                <td>Zeitanteil im Betrieb bei 25% Last (%)</td>
              </tr>
            </tbody>
          </table>
          <p><b>Hinweis:</b> Die Summe der Lastanteile muss 100% ergeben.</p>

          <h3>Methode 2: Systemwirkungsgrad</h3>
          <p>
            Diese Methode berechnet den Energiebedarf mit physikbasierter
            Modellierung über hydraulische und motorische Wirkungsgrade, Teillastverhalten,
            Affinitätsgesetze für Drehzahländerungen, explizite Drosselverluste und
            lastabhängige Frequenzumrichter-Wirkungsgrade.
          </p>
          <p>
            <b>Energiewandlungskette:</b> Netz → [Frequenzumrichter] → Motor → Getriebe
            → Pumpe → Hydraulische Nutzarbeit + Drosselverluste
          </p>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Nennleistung</td>
                <td>Auf dem Typenschild angegebene Nennleistung in kW</td>
              </tr>
              <tr>
                <td>Volumenstrom</td>
                <td>
                  Auslegungsvolumenstrom in m³/h.
                  <b>Achtung bei der EEI-Methode:</b> dort ist nicht der Auslegungspunkt
                  einzutragen, sondern der <b>Referenz-Betriebspunkt</b> — siehe unten.
                </td>
              </tr>
              <tr>
                <td>Förderhöhe</td>
                <td>
                  Auslegungsförderhöhe in m. Für die EEI-Methode gilt dasselbe wie beim
                  Volumenstrom.
                </td>
              </tr>
              <tr>
                <td>Nenndurchmesser DN</td>
                <td>
                  Nennweite der Pumpe in mm. <b>Optional und ohne Einfluss auf das Ergebnis.</b>
                  Der hydraulische Wirkungsgrad wird seit der Umstellung auf
                  Verordnung (EU) 547/2012 aus der <em>spezifischen Drehzahl</em>
                  (Q, H und Polzahl) bestimmt — die Baugröße allein sagt darüber nichts aus.
                  Das Feld bleibt nur zur Dokumentation erhalten.
                </td>
              </tr>
              <tr>
                <td>Wirkungsgrad im Bestpunkt (BEP)</td>
                <td>
                  Optional: Pumpen-Wirkungsgrad im Bestpunkt laut Datenblatt. Überschreibt die
                  Abschätzung aus DN/Leistung und verbessert die Genauigkeit deutlich.
                </td>
              </tr>
              <tr>
                <td>Getriebeart</td>
                <td>
                  Auswahl zwischen Direktantrieb, Flachriemen oder Keilriemen
                </td>
              </tr>
              <tr>
                <td>Regelung</td>
                <td>
                  Auswahl der Regelungsart: ungeregelt, zweistufig,
                  Konstantdruckregelung oder Proportionaldruckregelung
                </td>
              </tr>
              <tr>
                <td>Effizienzklasse</td>
                <td>Motoreffizienzklasse (IE1, IE2, IE3, IE4, IE5, etc.)</td>
              </tr>
              <tr>
                <td>Polzahl</td>
                <td>Polzahl des Motors (2, 4, 6, 8)</td>
              </tr>
              <tr>
                <td>Anteil Volllast</td>
                <td>Zeitanteil im Betrieb bei Volllast (%)</td>
              </tr>
              <tr>
                <td>Anteil 75% Last</td>
                <td>Zeitanteil im Betrieb bei 75% Last (%)</td>
              </tr>
              <tr>
                <td>Anteil 50% Last</td>
                <td>Zeitanteil im Betrieb bei 50% Last (%)</td>
              </tr>
              <tr>
                <td>Anteil 25% Last</td>
                <td>Zeitanteil im Betrieb bei 25% Last (%)</td>
              </tr>
            </tbody>
          </table>
          <p><b>Hinweis:</b> Die Summe der Lastanteile muss 100% ergeben.</p>

          <h3>Methode 3: Nassläufer EEI (EU 641/2009)</h3>
          <p>
            Für Nassläufer-Umwälzpumpen mit im Datenblatt angegebenem
            Energieeffizienzindex (EEI). Erforderlich sind EEI, Volumenstrom und Förderhöhe.
            Die Methode gilt für kleine Pumpen (1 W ≤ hydraulische Leistung ≤ 2500 W); für
            größere Pumpen bitte den Systemwirkungsgrad verwenden. Das Ergebnis basiert auf dem
            standardisierten Lastprofil (6 % / 15 % / 35 % / 44 % bei 100/75/50/25 % Volumenstrom),
            skaliert auf die eingegebenen Betriebsstunden.
          </p>
          <p class="callout">
            <b>Welcher Betriebspunkt einzutragen ist.</b> Anhang II(2)(1) der Verordnung
            definiert Q₁₀₀&nbsp;% und H₁₀₀&nbsp;% als den <b>Referenz-Betriebspunkt</b>: den
            Punkt auf der obersten Kennlinie, an dem das <b>Produkt Q · H maximal</b> ist.
            Das ist <em>nicht</em> der Auslegungspunkt Ihrer Anlage. Der Index EEI ist auf
            genau diesen Punkt bezogen; wird stattdessen ein Anlagen-Auslegungspunkt
            eingetragen, verschiebt sich das Ergebnis erheblich — an einem realen Datenblatt
            gemessen um <b>−51 %</b>.
          </p>
          <p>
            <b>Wie das Ergebnis zu lesen ist.</b> Anhang II(2)(8) definiert die zugrunde
            liegende Leistung als <em>regelungskompensierten</em> Wert, nicht als gemessene
            Aufnahme. Das Ergebnis ist daher der Verbrauch dieser Pumpe <b>unter
            standardisierter Regelung</b>, skaliert auf Ihre Betriebsstunden — nicht der
            Verbrauch unter Ihrem tatsächlichen Anlagen- und Regelverhalten.
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Berechnungsgrundlage und Daten</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <h3>Methode 1: Pumpenkennlinien Leistung</h3>
          <p>
            Der jährliche Energieverbrauch wird direkt aus den Leistungswerten bei
            verschiedenen Lastpunkten berechnet. Für jeden Lastpunkt wird die
            Betriebszeit aus dem Lastanteil ermittelt und mit der zugehörigen Leistung
            multipliziert.
          </p>
          <p>
            <b>Formel:</b> E<sub>Jahr</sub> = Σ (P<sub>i</sub> × t<sub>i</sub>) × Anzahl
          </p>
          <p>
            Dabei ist P<sub>i</sub> die Leistung am Lastpunkt i und t<sub>i</sub> die
            zugehörige Betriebszeit.
          </p>
          <p>
            <b>Vorteil:</b> Diese Methode umgeht die meisten Näherungen, da sie direkte
            Messwerte oder Herstellerangaben verwendet.
          </p>

          <h3>Methode 2: Systemwirkungsgrad</h3>
          <p>
            Diese Methode berechnet den Energiebedarf mit  physikbasierter
            Modellierung über hydraulische und motorische Wirkungsgrade, Teillastverhalten,
            Affinitätsgesetze für Drehzahländerungen, explizite Drosselverluste und
            lastabhängige Frequenzumrichter-Wirkungsgrade.
          </p>
          <p>
            <b>Energiewandlungskette (modelliert an jedem Lastpunkt):</b>
          </p>
          <p style="text-align: center; font-family: monospace;">
            Netz → [Frequenzumrichter] → Motor → Getriebe → Pumpe → Hydraulische Nutzarbeit + Drosselverluste
          </p>
          <p>
            Für jeden der vier Lastpunkte (100%, 75%, 50%, 25%) wird die tatsächliche
            Betriebsdrehzahl basierend auf der Regelungsstrategie berechnet und
            die Affinitätsgesetze angewendet.
          </p>

          <h4>Hydraulische Leistung</h4>
          <p>
            P<sub>hyd</sub> = ρ × g × Q × H / 3600 [kW]
          </p>
          <p>
            Dabei sind Q der Volumenstrom (m³/h), H die Förderhöhe (m), ρ die Dichte
            (kg/m³ - für Wasser bei 20°C: 1000 kg/m³) und g die Erdbeschleunigung (9,81 m/s²).
          </p>
          <p>
            Bei drehzahlgeregelten Pumpen wird die Leistung gemäß Affinitätsgesetzen
            angepasst: P ∝ n³
          </p>

          <h4>Hydraulischer Wirkungsgrad</h4>
          <p>
            Der hydraulische Wirkungsgrad η<sub>hyd</sub> wird über empirische Formeln
            aus Nennleistung und optional Nenndurchmesser DN ermittelt und ist abhängig
            vom Teillastbetrieb.
          </p>

          <h4>Motorwirkungsgrad</h4>
          <p>
            Der Motorwirkungsgrad η<sub>Motor</sub> wird aus der gewählten
            Effizienzklasse und Polzahl bestimmt. Im Teillastbetrieb wird der
            Wirkungsgrad entsprechend der Last angepasst.
          </p>

          <h4>Getriebewirkungsgrad</h4>
          <table>
            <thead>
              <tr>
                <th>Getriebeart</th>
                <th>Wirkungsgrad</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Direktantrieb</td>
                <td>100%</td>
              </tr>
              <tr>
                <td>Flachriemen</td>
                <td>93% + 0,01 × P<sub>kW</sub></td>
              </tr>
              <tr>
                <td>Keilriemen</td>
                <td>87,25% + 0,0175 × P<sub>kW</sub></td>
              </tr>
            </tbody>
          </table>

          <h4>Regelungsstrategien (Physikbasierte Modellierung)</h4>
          <p>
            Die Berechnung nutzt nun eine vollständig physikbasierte Modellierung der
            Regelungsstrategien mit Affinitätsgesetzen, diskreten Drehzahlstufen und
            lastabhängigen Frequenzumrichter-Wirkungsgraden.
          </p>

          <h5>Affinitätsgesetze für Drehzahländerungen</h5>
          <p>
            Bei Änderung der Pumpendrehzahl von n₁ auf n₂ gelten folgende physikalische
            Zusammenhänge:
          </p>
          <ul>
            <li><b>Volumenstrom:</b> Q₂/Q₁ = n₂/n₁ (proportional zur Drehzahl)</li>
            <li><b>Förderhöhe:</b> H₂/H₁ = (n₂/n₁)² (proportional zum Quadrat der Drehzahl)</li>
            <li><b>Leistung:</b> P₂/P₁ = (n₂/n₁)³ (proportional zur dritten Potenz der Drehzahl)</li>
          </ul>
          <p>
            <b>Beispiel:</b> Betrieb mit 80% Drehzahl → 80% Volumenstrom, 64% Förderhöhe,
            51% Leistung → ca. 49% Energieeinsparung gegenüber Drosselung!
          </p>

          <table>
            <thead>
              <tr>
                <th>Regelungsart</th>
                <th>Funktionsweise</th>
                <th>Energieeffizienz</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>Ungeregelt</b></td>
                <td>
                  Pumpe läuft mit konstanter Volldrehzahl. Mengenregelung über
                  Drosselventil. Überschüssiger Druck wird am Ventil vernichtet.
                  Energieverlust: E = ρ·g·Q·ΔH·t
                </td>
                <td>
                  Geringstes Effizienz, höchster Energieverbrauch. Einfachste und
                  kostengünstigste Lösung.
                </td>
              </tr>
              <tr>
                <td><b>Zweistufig</b></td>
                <td>
                  Motor kann zwischen zwei festen Drehzahlen umgeschaltet werden
                  (100% und 67% Drehzahl durch Polumschaltung). Regelungslogik
                  wählt niedrigste Drehzahl, die den Bedarf deckt. Affinitätsgesetze:
                  bei 67% Drehzahl → 30% der Vollast-Leistung. Rest-Drosselung
                  zwischen den Drehzahlstufen.
                </td>
                <td>
                  Mittlere Effizienz. Deutliche Verbesserung gegenüber ungeregelt,
                  aber nicht optimal wegen diskreter Stufen.
                </td>
              </tr>
              <tr>
                <td><b>Konstantdruck-regelung (VSD)</b></td>
                <td>
                  Stufenlose Drehzahlregelung über Frequenzumrichter (20-100% Bereich).
                  Hält konstanten Druck aufrecht (H = H_Auslegung). Drehzahl passt
                  sich automatisch an Durchfluss an: n ∝ Q. Ideal für Anlagen mit
                  konstantem Druckbedarf (z.B. Förderanlagen, Druckhaltung).
                </td>
                <td>
                  Hohe Effizienz durch optimale Drehzahlanpassung. Minimale
                  Drosselverluste. FU-Wirkungsgrad: 92-96% lastabhängig.
                </td>
              </tr>
              <tr>
                <td><b>Proportionaldruck-regelung (VSD)</b></td>
                <td>
                  Stufenlose Drehzahlregelung über Frequenzumrichter. Druck variiert
                  mit Durchfluss gemäß Anlagenkennlinie: H ∝ Q² (reibungsdominierte
                  Systeme). Optimale Anpassung von Drehzahl und Druck an Bedarf.
                  Ideal für Heizungs-/Kälteanlagen.
                </td>
                <td>
                  Höchste Effizienz aller Regelungsarten. Kombination aus
                  Affinitätsgesetzen und Anlagenkennlinie. FU-Wirkungsgrad: 92-96%.
                </td>
              </tr>
            </tbody>
          </table>

          <h5>Frequenzumrichter-Wirkungsgrad (VSD)</h5>
          <p>
            Der Wirkungsgrad von Frequenzumrichtern ist lastabhängig und wird nun
            dynamisch berechnet (nicht mehr konstant 95%):
          </p>
          <table>
            <thead>
              <tr>
                <th>Motorlast</th>
                <th>FU-Wirkungsgrad</th>
                <th>Verlustmechanismus</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>25%</td>
                <td>~92%</td>
                <td>Schaltverluste dominieren</td>
              </tr>
              <tr>
                <td>50%</td>
                <td>~94%</td>
                <td>Ausgeglichenes Verhältnis</td>
              </tr>
              <tr>
                <td>75%</td>
                <td>~95%</td>
                <td>Durchlassverluste steigend</td>
              </tr>
              <tr>
                <td>100%</td>
                <td>~96%</td>
                <td>Durchlassverluste dominieren</td>
              </tr>
            </tbody>
          </table>
          <p>
            <i>Basis: ABB/Siemens Wirkungsgradkurven für Industrieantriebe (5-100 kW)</i>
          </p>

          <h5>Mehrstufige Drehzahlsteuerung</h5>
          <p>
            Bei zweistufiger Regelung stehen zwei feste Drehzahlen zur Verfügung:
          </p>
          <ul>
            <li><b>100% Drehzahl</b> (Vollast)</li>
            <li><b>67% Drehzahl</b> (entspricht 2-Pol/3-Pol-Äquivalent)</li>
          </ul>
          <p>
            Die Steuerung wählt die niedrigste Drehzahl, die den Durchflussbedarf deckt.
            Zwischen den Stufen erfolgt Rest-Drosselung.
          </p>

          <h4>Energieeffizienzklassen für Motoren (IE)</h4>
          <table>
            <thead>
              <tr>
                <th>Klasse</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>IE1</td>
                <td>Standard Wirkungsgrad (vergleichbar EFF2)</td>
              </tr>
              <tr>
                <td>IE2</td>
                <td>Hoher Wirkungsgrad (vergleichbar EFF1)</td>
              </tr>
              <tr>
                <td>IE3</td>
                <td>Premium Wirkungsgrad</td>
              </tr>
              <tr>
                <td>IE4</td>
                <td>Super Premium Efficiency</td>
              </tr>
              <tr>
                <td>IE5</td>
                <td>Ultra Premium Efficiency</td>
              </tr>
            </tbody>
          </table>
          <p>
            <b>Vorschriften:</b> Seit Juli 2021 müssen Dreiphasenmotoren mit einer
            Nennausgangsleistung zwischen 0,75 kW und höchstens 1000 kW das
            Effizienzniveau IE3 aufweisen. Ab Juli 2023 ist für Motoren zwischen 75 kW
            und 200 kW das Niveau IE4 verpflichtend.
          </p>

          <h4>Hydraulischer Wirkungsgrad nach MEI</h4>
          <p>
            Der hydraulische Wirkungsgrad wird am Mindesteffizienzindex MEI gemessen
            (gem. Verordnung (EU) 547/2012). Seit dem 01.01.2015 wird ein MEI &gt; 0,4
            gefordert.
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Zusätzliche Informationen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <v-container>
            <h3>Physikbasierte Berechnungsgrundlage</h3>
            <p>
              Die aktuelle Implementierung nutzt eine vollständig physikbasierte Modellierung:
            </p>
            <ul>
              <li><b>Affinitätsgesetze:</b> Korrekte Anwendung für Drehzahländerungen (Q∝n, H∝n², P∝n³)</li>
              <li><b>Diskrete Drehzahlstufen:</b> Zweistufige Regelung mit realen Geschwindigkeiten [100%, 67%]</li>
              <li><b>Explizite Drosselverluste:</b> Berechnung von E = ρ·g·Q·ΔH·t</li>
              <li><b>VSD-Wirkungsgradkurve:</b> Lastabhängig 92-96% statt konstant 95%</li>
              <li><b>Pumpen-/Anlagenkennlinie:</b> Berücksichtigung von H(Q)-Zusammenhängen</li>
            </ul>

            <h4>Energieeinsparung nach Regelungsart (Beispiel: 10 kW, 60% Durchschnittslast)</h4>
            <table>
              <thead>
                <tr>
                  <th>Regelungsart</th>
                  <th>Energieverbrauch</th>
                  <th>Einsparung vs. ungeregelt</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Ungeregelt (Drosselung)</td>
                  <td>100%</td>
                  <td>—</td>
                </tr>
                <tr>
                  <td>Zweistufig</td>
                  <td>~73%</td>
                  <td>~27%</td>
                </tr>
                <tr>
                  <td>VSD Konstantdruck</td>
                  <td>~56%</td>
                  <td>~44%</td>
                </tr>
                <tr>
                  <td>VSD Proportionaldruck</td>
                  <td>~43%</td>
                  <td>~57%</td>
                </tr>
              </tbody>
            </table>
            <p>
              <i>Hinweis: Tatsächliche Einsparungen hängen vom Lastprofil ab. Bei variablen
              Lasten sind die Vorteile von VSD noch größer.</i>
            </p>

            <h3>Optimierungsmöglichkeiten</h3>
            <p>
              Folgende Maßnahmen haben wesentlichen Einfluss auf die Energiekosten einer Pumpe:
            </p>
            <table>
              <thead>
                <tr>
                  <th>Kategorie</th>
                  <th>Beschreibung</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Motor</td>
                  <td>Austausch der Pumpe durch eine neue energieeffiziente Pumpe</td>
                </tr>
                <tr>
                  <td>Dimensionierung</td>
                  <td>Anpassen der Pumpengröße an den tatsächlichen Bedarf</td>
                </tr>
                <tr>
                  <td>Regelung / Steuerung</td>
                  <td>Einsatz einer stufenlosen Drehzahlregelung (VSD)</td>
                </tr>
                <tr>
                  <td>Betriebszeit</td>
                  <td>Anpassung der Betriebszeit an die tatsächlichen Nutzungszeiten</td>
                </tr>
                <tr>
                  <td>Verbraucher</td>
                  <td>Systemoptimierung zur Reduzierung des Bedarfs</td>
                </tr>
              </tbody>
            </table>

            <h3>Energieeffizienzklassen für Nassläuferpumpen (EEI)</h3>
            <table>
              <thead>
                <tr>
                  <th>Klasse</th>
                  <th>Energie Effizienz Index (EEI)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>A+++</td>
                  <td>EEI &lt; 0,1</td>
                </tr>
                <tr>
                  <td>A++</td>
                  <td>0,1 ≤ EEI &lt; 0,16</td>
                </tr>
                <tr>
                  <td>A+</td>
                  <td>0,16 ≤ EEI &lt; 0,23</td>
                </tr>
                <tr>
                  <td>A</td>
                  <td>0,23 ≤ EEI &lt; 0,3</td>
                </tr>
                <tr>
                  <td>B</td>
                  <td>0,3 ≤ EEI &lt; 0,42</td>
                </tr>
                <tr>
                  <td>C</td>
                  <td>0,42 ≤ EEI &lt; 0,6</td>
                </tr>
                <tr>
                  <td>D</td>
                  <td>0,6 ≤ EEI &lt; 0,8</td>
                </tr>
                <tr>
                  <td>E</td>
                  <td>0,8 ≤ EEI &lt; 0,9</td>
                </tr>
                <tr>
                  <td>F</td>
                  <td>0,9 ≤ EEI &lt; 1</td>
                </tr>
                <tr>
                  <td>G</td>
                  <td>1 ≤ EEI</td>
                </tr>
              </tbody>
            </table>
            <p>
              Nach ErP-Richtlinie für Nassläufer-/Umwälzpumpen (Verordnung (EG)
              641/2009 und (EU) 622/2012) müssen alle neuen Umwälzpumpen ab August 2015
              einen mindest-Energieeffizienzindex von 0,23 erfüllen.
            </p>

            <h3>Neue Pumpentechnologie</h3>
            <p>
              Moderne Hocheffizienzpumpen mit EC-Motorentechnologie (z.B. Wilo-Stratos,
              Grundfos Magna, KSB Rio-Eco, DAB EVOPLUS) erreichen deutlich bessere
              Wirkungsgrade als konventionelle AC-Motoren, insbesondere im
              Teillastbetrieb.
            </p>

            <p>
              Elektromotoren sind in Europa die wichtigsten Stromverbraucher: Sie sind
              für rund 70% des industriellen Stromverbrauchs und ein Drittel des
              Stromverbrauchs im Dienstleistungssektor verantwortlich. Fast 20% des
              weltweiten Stromverbrauchs von Motoren entfallen dabei auf Pumpen.
              Stromkosten machen den größten Teil der Lebenszykluskosten einer Pumpe aus
              – bis zu 85%.
            </p>
          </v-container>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Quellen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <h2>Quellen</h2>
          <ul>
            <li>
              DIN V 18599-5:2018-09 §6.3.2 (Heizungs-Umwälzpumpen; Tabelle 28
              Regelungsfaktoren, Gl. (56) Pumpenmanagement, Gl. (63) Typenschild im Bestand)
            </li>
            <li>
              DIN V 18599-7:2018-09 Anhang D (Kaltwasserpumpen; D.2.6 hydraulischer Abgleich,
              Tabelle D.8 Betriebszeiten)
            </li>
            <li>DIN V 18599-8:2018-09 §6.2.2.4 (Zirkulationspumpen)</li>
            <li>
              Verordnung (EU) 547/2012, Anhang III (MEI, Mindestwirkungsgrad im Bestpunkt) —
              in der Fassung nach EN 16480
            </li>
            <li>Verordnung (EG) 641/2009 und (EU) 622/2012 (EEI-Anforderungen)</li>
            <li>IEC 60034-30-1 (Motorwirkungsgradklassen IE1–IE5)</li>
            <li>EU-Richtlinie 2019/1781</li>
            <li>ZVEI-Publikation „Motoren und geregelte Antriebe"</li>
            <li>ZVEI-Publikation „Energiesparen mit elektrischen Antrieben"</li>
          </ul>
          <p>
            <b>Hinweis:</b> Frühere Fassungen dieser Hilfe nannten <em>DIN SPEC 15240</em> als
            Quelle. Diese Norm behandelt die Inspektion von Klimaanlagen und Lüftung, nicht
            Pumpen; sie war zu keinem Zeitpunkt Grundlage der Pumpenberechnung und wurde
            deshalb entfernt.
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

  </div>
</template>

<style scoped>
/* Der Genauigkeitsüberblick nutzt bewusst die normale Tabellendarstellung der übrigen
   Panels — keine eigenen Farben, Badges oder Rahmen. Hier bleibt nur ein Überlaufschutz
   für schmale Fenster. */
.precision {
  overflow-x: auto;
}

/* Hervorgehobener Hinweis für Angaben, deren Missachtung das Ergebnis deutlich verschiebt:
   Referenz-Betriebspunkt bei EEI, der Nutzen-Vorbehalt und die veralteten Varianten. */
.callout {
  margin: 0.75rem 0;
  padding: 0.7rem 0.9rem;
  border-radius: 8px;
  border-left: 4px solid rgb(var(--v-theme-warning));
  background-color: rgba(var(--v-theme-warning), 0.1);
  line-height: 1.55;
}
</style>